import {
  blue,
  emerald,
  amber,
  orange,
  purple,
  red,
  slate,
} from '@/lib/colours';

/**
 * Hardcoded dark chrome for the landing “every angle” demos. Independent of
 * the app colour scheme so the section always matches the marketing reference.
 */
export const landingDemo = {
  sectionBg: slate[950],
  panelBg: slate[800],
  panelBorder: slate[700],
  divider: `${blue[500]}33`,
  ink: slate[50],
  inkSoft: slate[400],
  inkMuted: slate[500],
  accent: blue[300],
  accentStrong: blue[500],
  sire: blue[400],
  dam: '#F472B6',
  cardBg: slate[900],
  toggleSelectedBg: blue[600],
  toggleSelectedInk: slate[50],
} as const;

export type LandingGradientFamily =
  'teal' | 'emerald' | 'amber' | 'orange' | 'purple' | 'red';

/** Same stop recipe as HerdMASTER `chartGradients` BAR_GRADIENTS (`gb-*`). */
const LANDING_BAR_GRADIENTS: {
  id: string;
  family: LandingGradientFamily;
  stops: { offset: string; color: string }[];
}[] = [
  {
    id: 'landing-gb-teal',
    family: 'teal',
    stops: [
      { offset: '0%', color: blue[400] },
      { offset: '100%', color: blue[700] },
    ],
  },
  {
    id: 'landing-gb-orange',
    family: 'orange',
    stops: [
      { offset: '0%', color: orange[400] },
      { offset: '100%', color: orange[700] },
    ],
  },
  {
    id: 'landing-gb-emerald',
    family: 'emerald',
    stops: [
      { offset: '0%', color: emerald[400] },
      { offset: '100%', color: emerald[700] },
    ],
  },
  {
    id: 'landing-gb-amber',
    family: 'amber',
    stops: [
      { offset: '0%', color: amber[400] },
      { offset: '100%', color: amber[700] },
    ],
  },
  {
    id: 'landing-gb-purple',
    family: 'purple',
    stops: [
      { offset: '0%', color: purple[400] },
      { offset: '100%', color: purple[700] },
    ],
  },
  {
    id: 'landing-gb-red',
    family: 'red',
    stops: [
      { offset: '0%', color: red[400] },
      { offset: '100%', color: red[700] },
    ],
  },
];

const FAMILY_ORDER: LandingGradientFamily[] = [
  'teal',
  'orange',
  'emerald',
  'amber',
  'purple',
  'red',
];

/** Legend swatch solids — mid-tone of each `gb-*` family. */
export const landingChartSwatch: Record<LandingGradientFamily, string> = {
  teal: blue[500],
  orange: orange[500],
  emerald: emerald[500],
  amber: amber[500],
  purple: purple[500],
  red: red[500],
};

export function landingFamilyByIndex(i: number): LandingGradientFamily {
  return FAMILY_ORDER[i % FAMILY_ORDER.length] ?? 'teal';
}

export function landingGradientUrl(family: LandingGradientFamily): string {
  return `url(#landing-gb-${family})`;
}

/** SVG defs for the landing donut — scoped ids so they never clash with app charts. */
export function LandingChartGradients() {
  return (
    <defs>
      {LANDING_BAR_GRADIENTS.map((g) => (
        <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
          {g.stops.map((s, i) => (
            <stop
              key={i}
              offset={s.offset}
              stopColor={s.color}
              stopOpacity={1}
            />
          ))}
        </linearGradient>
      ))}
    </defs>
  );
}
